import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Campo } from '@/componentes/campo';
import { Segmentado, type OpcaoSegmentada } from '@/componentes/segmentado';
import { Button } from '@/componentes/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card';
import { Input } from '@/componentes/ui/input';
import { ErroApi, mensagemDoErro, useEntrar, useEu, useRegistrar } from '@/dados';
import { emailValido } from '@/lib/email';

/* Entrar — tela OPCIONAL, e a única do produto que não faz parte do funil.
 *
 * Ninguém é obrigado a se identificar para usar a ferramenta: a sessão anônima
 * já está criada quando esta tela abre. Conta serve para acessar os mesmos
 * dados de outro aparelho, e criar uma PROMOVE a sessão corrente (mesmo id no
 * banco), então nada do que já foi preenchido se perde.
 *
 * ── AS TRÊS DECISÕES DE PRODUTO QUE ESTE FORMULÁRIO CARREGA ───────────────
 * 1. Não há confirmação por e-mail. A conta nasce válida e a sessão já entra
 *    no mesmo POST, porque o cadastro aqui não é porta de entrada: é backup de
 *    um trabalho que já existe. Mandar a pessoa para a caixa de entrada no
 *    meio de uma precificação é perder a precificação.
 * 2. Por isso a senha é digitada DUAS vezes. Sem confirmação de e-mail e sem
 *    recuperação de senha (ainda não existe), um erro de digitação na senha
 *    tranca a conta para sempre, com os dados dentro. A repetição é a única
 *    rede que existe hoje, e é o motivo de ela não ser opcional.
 * 3. Entrar e criar conta são o MESMO formulário, num alternador, e não duas
 *    telas: são os mesmos dois campos.
 */

type Modo = 'criar' | 'entrar';

type Formulario = { email: string; senha: string; repetir: string };
type Erros = Partial<Record<keyof Formulario, string>>;

/** O mesmo mínimo que a API cobra (`credenciais`, em auth.rotas.ts). */
const SENHA_MINIMA = 8;

const OPCOES: ReadonlyArray<OpcaoSegmentada<Modo>> = [
  { valor: 'criar', rotulo: 'Criar conta' },
  { valor: 'entrar', rotulo: 'Já tenho conta' },
];

const DESCRICAO: Record<Modo, string> = {
  criar:
    'A conta guarda o que já foi preenchido nesta sessão e libera o acesso de outro computador.',
  entrar:
    'Informe o e-mail e a senha da conta. O que estiver preenchido nesta sessão de visitante fica de fora.',
};

export function Entrar() {
  const eu = useEu();
  const navegar = useNavigate();

  const [modo, setModo] = React.useState<Modo>('criar');
  const [form, setForm] = React.useState<Formulario>({ email: '', senha: '', repetir: '' });
  const [erros, setErros] = React.useState<Erros>({});

  const criar = useRegistrar();
  const acessar = useEntrar();
  const envio = modo === 'criar' ? criar : acessar;
  const enviando = envio.isPending;

  function alterar<C extends keyof Formulario>(campo: C, valor: string) {
    setForm((atual) => ({ ...atual, [campo]: valor }));
    // O erro some quando a pessoa mexe no campo: manter a mensagem embaixo de
    // um campo já corrigido é ruído.
    setErros((atual) => (atual[campo] === undefined ? atual : { ...atual, [campo]: undefined }));
  }

  /* Trocar de modo preserva o que foi digitado (é o mesmo e-mail, e às vezes a
     mesma senha) e apaga o que era resposta do modo anterior: erro de campo e
     erro de servidor. Sem o reset, "Já existe uma conta com este e-mail" ficava
     na tela depois de virar login. */
  function trocarModo(proximo: Modo) {
    setModo(proximo);
    setErros({});
    criar.reset();
    acessar.reset();
  }

  async function aoEnviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (enviando) return;

    const achados = validar(modo, form);
    setErros(achados);
    if (Object.keys(achados).length > 0) return;

    const credenciais = { email: form.email.trim(), senha: form.senha };
    try {
      await (modo === 'criar'
        ? criar.mutateAsync(credenciais)
        : acessar.mutateAsync(credenciais));
      navegar('/');
    } catch {
      // A mensagem já está em `envio.error`, desenhada abaixo dos campos. O que
      // importa aqui é NÃO navegar: sair da tela apagaria o que foi digitado.
    }
  }

  // Conta real já autenticada não pode registrar nem trocar de conta às cegas:
  // a API recusa com 409, e mostrar o formulário seria oferecer um caminho que
  // termina em erro.
  if (eu.data && !eu.data.anonimo) return <JaEstaNaConta email={eu.data.email} />;

  const emailEmUso = envio.error instanceof ErroApi && envio.error.codigo === 'EMAIL_EM_USO';

  return (
    <Casco>
      <CardHeader>
        <CardTitle>{modo === 'criar' ? 'Criar conta' : 'Entrar na sua conta'}</CardTitle>
        <CardDescription>{DESCRICAO[modo]}</CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={aoEnviar} className="grid gap-4" noValidate>
          <Segmentado
            rotulo="O que você quer fazer"
            rotuloOculto
            opcoes={OPCOES}
            valor={modo}
            aoMudar={trocarModo}
            className="justify-items-center"
          />

          <Campo rotulo="E-mail" obrigatorio erro={erros.email}>
            {(atributos) => (
              <Input
                {...atributos}
                type="email"
                value={form.email}
                onChange={(e) => alterar('email', e.currentTarget.value)}
                disabled={enviando}
                autoComplete="email"
                placeholder="voce@suaempresa.com.br"
              />
            )}
          </Campo>

          <CampoSenha
            rotulo="Senha"
            valor={form.senha}
            aoMudar={(valor) => alterar('senha', valor)}
            erro={erros.senha}
            disabled={enviando}
            ajuda={modo === 'criar' ? `Mínimo de ${SENHA_MINIMA} caracteres.` : undefined}
            autoComplete={modo === 'criar' ? 'new-password' : 'current-password'}
          />

          {modo === 'criar' ? (
            <CampoSenha
              rotulo="Repetir a senha"
              valor={form.repetir}
              aoMudar={(valor) => alterar('repetir', valor)}
              erro={erros.repetir}
              disabled={enviando}
              autoComplete="new-password"
            />
          ) : null}

          {envio.isError ? (
            <div role="alert" className="text-negativo grid justify-items-start gap-2 text-apoio">
              <p>{mensagemDoErro(envio.error)}</p>
              {/* O e-mail já cadastrado quase sempre é a própria pessoa
                  voltando. O caminho de saída fica no erro, com o que foi
                  digitado preservado, em vez de mandá-la achar o alternador. */}
              {emailEmUso ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => trocarModo('entrar')}
                >
                  Entrar com este e-mail
                </Button>
              ) : null}
            </div>
          ) : null}

          <Button type="submit" disabled={enviando}>
            {rotuloDoBotao(modo, enviando)}
          </Button>

          <Button variant="ghost" asChild>
            <Link to="/">Continuar sem entrar</Link>
          </Button>
        </form>
      </CardContent>
    </Casco>
  );
}

/* ── Peças locais ─────────────────────────────────────────────────────────── */

/* Os dois campos de senha são o MESMO controle; muda o rótulo e o autoComplete.
 *
 * `autoComplete` é prop e não valor fixo porque os dois valores fazem coisas
 * opostas: `new-password` faz o gerenciador de senhas OFERECER uma senha forte,
 * `current-password` faz ele PREENCHER a que já existe. Trocados, o navegador
 * "para de preencher" e o sintoma não aponta para aqui. */
function CampoSenha({
  rotulo,
  valor,
  aoMudar,
  erro,
  ajuda,
  autoComplete,
  disabled,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  erro?: string | undefined;
  ajuda?: string | undefined;
  autoComplete: 'new-password' | 'current-password';
  disabled?: boolean | undefined;
}) {
  return (
    <Campo rotulo={rotulo} obrigatorio erro={erro} ajuda={ajuda}>
      {(atributos) => (
        <Input
          {...atributos}
          type="password"
          value={valor}
          onChange={(e) => aoMudar(e.currentTarget.value)}
          disabled={disabled}
          autoComplete={autoComplete}
        />
      )}
    </Campo>
  );
}

/** O casco desta tela: cartão único, centrado, sem trilha do funil. */
function Casco({ children }: { children: React.ReactNode }) {
  return (
    <main className="grid min-h-screen place-items-center p-6">
      <Card className="w-full max-w-sm">{children}</Card>
    </main>
  );
}

function JaEstaNaConta({ email }: { email: string | null }) {
  return (
    <Casco>
      <CardHeader>
        <CardTitle>Você já está em uma conta</CardTitle>
        <CardDescription>
          A sessão é de {email ?? 'uma conta com e-mail'}. Para usar outra, saia pelo menu da conta
          e volte aqui.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild className="w-full">
          <Link to="/">Voltar ao preço</Link>
        </Button>
      </CardContent>
    </Casco>
  );
}

/* ── Regras do formulário ─────────────────────────────────────────────────── */

function rotuloDoBotao(modo: Modo, enviando: boolean): string {
  if (modo === 'criar') return enviando ? 'Criando a conta…' : 'Criar conta e entrar';
  return enviando ? 'Entrando…' : 'Entrar';
}

function validar(modo: Modo, form: Formulario): Erros {
  const erros: Erros = {};

  if (!emailValido(form.email)) {
    erros.email = 'Informe um e-mail no formato voce@suaempresa.com.br.';
  }
  if (form.senha.length < SENHA_MINIMA) {
    erros.senha = `A senha precisa de pelo menos ${SENHA_MINIMA} caracteres.`;
  }
  // Só no cadastro: no login a senha certa é a que o servidor conhece, e um
  // segundo campo ali seria atrito sem ganho nenhum.
  if (modo === 'criar' && form.repetir !== form.senha) {
    erros.repetir = 'As duas senhas precisam ser iguais.';
  }

  return erros;
}
